import {
  capitalizeTerm,
  type StorefrontFeatureKey,
  type StorefrontTerminology,
} from "@loot-os/api-contract";
import {
  AnvilIcon,
  BackpackIcon,
  BoxIcon,
  type LucideIcon,
  ShieldCheckIcon,
  SwordsIcon,
  UserIcon,
} from "lucide-react";

/**
 * The storefront's links. `group` places one: the games sit in the header's Games menu and the
 * phone's tab bar, the other pages beside them. `feature` hides a link when the tenant has it off,
 * and `label` reads the store's own terms, so a store that sells "packs" lists "Packs". Vending
 * joins the games once it has a page and a feature switch.
 */
// `as const` keeps `to` a literal so the router type-checks each path; `satisfies` alone widens it.
export const NAV_ITEMS = [
  {
    to: "/boxes",
    group: "games",
    icon: BoxIcon,
    label: (t: StorefrontTerminology) => capitalizeTerm(t.box.plural),
    exact: false,
    feature: undefined,
  },
  {
    to: "/battles",
    group: "games",
    icon: SwordsIcon,
    label: (t: StorefrontTerminology) => capitalizeTerm(t.battle.plural),
    exact: false,
    feature: "battles",
  },
  {
    to: "/forge",
    group: "games",
    icon: AnvilIcon,
    label: (t: StorefrontTerminology) => capitalizeTerm(t.forge.singular),
    exact: false,
    feature: "forge",
  },
  {
    to: "/inventory",
    group: "pages",
    icon: BackpackIcon,
    label: (t: StorefrontTerminology) => capitalizeTerm(t.inventory.singular),
    exact: false,
    feature: undefined,
  },
  {
    to: "/fairness",
    group: "pages",
    icon: ShieldCheckIcon,
    label: () => "Fairness",
    exact: false,
    feature: undefined,
  },
  {
    to: "/account",
    group: "pages",
    icon: UserIcon,
    label: () => "Account",
    exact: false,
    feature: undefined,
  },
] as const satisfies readonly {
  to: string;
  group: "games" | "pages";
  icon: LucideIcon;
  label: (terms: StorefrontTerminology) => string;
  exact: boolean;
  feature?: StorefrontFeatureKey;
}[];

/** A link as the header, the menu, the tab bar and the footer render it, in the store's words. */
export interface NavLink {
  to: (typeof NAV_ITEMS)[number]["to"];
  group: "games" | "pages";
  icon: LucideIcon;
  label: string;
  exact: boolean;
}

export function visibleNavItems(
  hasFeature: (key: StorefrontFeatureKey) => boolean,
  terms: StorefrontTerminology,
): NavLink[] {
  return NAV_ITEMS.filter((item) => !item.feature || hasFeature(item.feature)).map((item) => ({
    to: item.to,
    group: item.group,
    icon: item.icon,
    label: item.label(terms),
    exact: item.exact,
  }));
}
